import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";
import {
  CurrencyCircleDollar,
  Receipt,
  Crown,
  ChartLineUp,
  FileCsv,
  ArrowLeft,
  ArrowRight,
} from "@phosphor-icons/react";
import { api, formatBRL, formatNum, formatDate, BACKEND_URL } from "@/lib/gado";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const PAGE_SIZE = 15;

const STATUS_STYLE = {
  paid: "border-gado-green/30 bg-gado-green/10 text-gado-green",
  pending: "border-gado-gold/40 bg-gado-gold/10 text-gado-gold",
  failed: "border-destructive/40 bg-destructive/10 text-destructive",
};
const STATUS_LABEL = { paid: "Pago", pending: "Pendente", failed: "Falhou" };
const PLAN_LABEL = { gadopro_pro_monthly: "Pro mensal", gadopro_pro_yearly: "Pro anual" };

function Kpi({ icon: Icon, label, value, sub, accent, testid }) {
  return (
    <div className="rounded-xl border border-gado-line bg-white p-4 min-w-0" data-testid={testid}>
      <div className="flex items-center gap-2 mb-1.5">
        <span className={`h-7 w-7 rounded-lg grid place-items-center shrink-0 ${accent ? "bg-gado-gold/15 text-gado-gold" : "bg-gado-green/10 text-gado-green"}`}>
          <Icon size={15} weight="fill" />
        </span>
        <p className="text-[10px] uppercase tracking-[0.14em] text-gado-muted font-semibold truncate">{label}</p>
      </div>
      <p className="font-cabinet text-xl md:text-2xl font-extrabold text-gado-ink tabular-nums truncate">{value}</p>
      {sub && <p className="text-[11px] text-gado-muted mt-0.5 truncate">{sub}</p>}
    </div>
  );
}

export default function AdminFinance() {
  const [status, setStatus] = useState("todos");
  const [page, setPage] = useState(1);

  const { data: ov, isLoading: loadingOv } = useQuery({
    queryKey: ["admin-finance-overview"],
    queryFn: async () => (await api.get("/admin/finance/overview")).data,
  });

  const { data: payments, isLoading: loadingPay } = useQuery({
    queryKey: ["admin-finance-payments", status, page],
    queryFn: async () =>
      (await api.get("/admin/finance/payments", {
        params: { status: status === "todos" ? undefined : status, page, page_size: PAGE_SIZE },
      })).data,
  });

  const exportCsv = async () => {
    try {
      const token = localStorage.getItem("gadopro_token");
      const resp = await fetch(`${BACKEND_URL}/api/admin/finance/export.csv`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) throw new Error();
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "financeiro_gadopro.csv";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("CSV exportado");
    } catch {
      toast.error("Falha ao exportar");
    }
  };

  const items = payments?.items || [];
  const total = payments?.total || 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="space-y-5" data-testid="admin-finance-page">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="font-cabinet text-2xl md:text-3xl font-bold text-gado-ink tracking-tight">Financeiro</h1>
          <p className="text-gado-muted text-sm">Receita real dos pagamentos Stripe (ambiente de teste).</p>
        </div>
        <Button onClick={exportCsv} variant="outline" data-testid="finance-export-csv" className="shrink-0">
          <FileCsv size={16} className="mr-1.5" /> Exportar CSV
        </Button>
      </div>

      {loadingOv || !ov ? (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[...Array(4)].map((_, i) => <div key={i} className="h-24 rounded-xl bg-gado-line/40 animate-pulse" />)}
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Kpi icon={CurrencyCircleDollar} label="Receita confirmada" value={formatBRL(ov.receita_total)} sub={`${ov.pagamentos_pagos} pagamento(s)`} accent testid="fin-receita-total" />
            <Kpi icon={ChartLineUp} label="Receita no mês" value={formatBRL(ov.receita_mes)} sub="pagamentos confirmados" testid="fin-receita-mes" />
            <Kpi icon={Crown} label="Assinaturas ativas" value={formatNum(ov.assinaturas_ativas, 0)} sub="usuários no plano Pro" accent testid="fin-assinaturas" />
            <Kpi icon={Receipt} label="Ticket médio" value={formatBRL(ov.ticket_medio)} sub={`${ov.pagamentos_total} transação(ões) no total`} testid="fin-ticket" />
          </div>

          <div className="rounded-xl border border-gado-line bg-white p-4 md:p-5" data-testid="fin-chart">
            <p className="text-[10px] uppercase tracking-[0.2em] text-gado-muted font-semibold mb-3">Receita confirmada por mês</p>
            <div className="h-44 md:h-52 min-w-[1px]">
              <ResponsiveContainer width="100%" height="100%" minWidth={1} minHeight={1}>
                <BarChart data={ov.chart_receita} margin={{ top: 4, right: 4, left: -14, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#EDEFEA" />
                  <XAxis dataKey="mes" tickLine={false} axisLine={false} tick={{ fontSize: 11, fill: "#5C6A60" }} />
                  <YAxis tickLine={false} axisLine={false} tick={{ fontSize: 11, fill: "#5C6A60" }} />
                  <Tooltip
                    cursor={{ fill: "#F3F5F1" }}
                    formatter={(v) => formatBRL(v)}
                    contentStyle={{ borderRadius: 10, border: "1px solid #E8EAE6", fontSize: 12 }}
                  />
                  <Bar dataKey="valor" fill="#C8963E" radius={[6, 6, 0, 0]} maxBarSize={36} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </>
      )}

      {/* Pagamentos */}
      <div className="flex items-center justify-between gap-3">
        <p className="text-xs font-bold uppercase tracking-[0.16em] text-gado-muted">Transações</p>
        <Select value={status} onValueChange={(v) => { setStatus(v); setPage(1); }}>
          <SelectTrigger className="w-36 bg-white" data-testid="finance-status-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="todos">Todas</SelectItem>
            <SelectItem value="paid">Pagas</SelectItem>
            <SelectItem value="pending">Pendentes</SelectItem>
            <SelectItem value="failed">Falhas</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2" data-testid="finance-payments-list">
        {loadingPay && <p className="text-sm text-gado-muted text-center py-8">Carregando…</p>}
        {!loadingPay && items.length === 0 && (
          <div className="rounded-xl border border-gado-line bg-white text-center py-12">
            <Receipt size={32} className="mx-auto text-gado-line mb-2" />
            <p className="text-sm text-gado-muted">Nenhuma transação encontrada.</p>
          </div>
        )}
        {items.map((p) => (
          <div
            key={p.session_id}
            className="rounded-xl border border-gado-line bg-white px-3.5 py-3 flex items-center justify-between gap-3 min-w-0"
            data-testid={`payment-${p.session_id}`}
          >
            <div className="min-w-0">
              <p className="text-sm font-semibold text-gado-ink truncate">
                {p.customer_email || "Cliente removido"}
              </p>
              <p className="text-xs text-gado-muted truncate">
                {formatDate(p.created_at)} · {PLAN_LABEL[p.lookup_key] || p.lookup_key || "—"}
              </p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <span className="font-mono text-sm font-bold text-gado-ink tabular-nums">{formatBRL(p.amount)}</span>
              <Badge variant="outline" className={`text-[10px] ${STATUS_STYLE[p.payment_status] || ""}`}>
                {STATUS_LABEL[p.payment_status] || p.payment_status}
              </Badge>
            </div>
          </div>
        ))}
      </div>

      <div className="flex items-center justify-between">
        <p className="text-xs text-gado-muted">{total} transação(ões) · página {page} de {totalPages}</p>
        <div className="flex gap-1.5">
          <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} data-testid="fin-page-prev">
            <ArrowLeft size={14} />
          </Button>
          <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)} data-testid="fin-page-next">
            <ArrowRight size={14} />
          </Button>
        </div>
      </div>
    </div>
  );
}
